/**
 * HotelBrands displays a list of hotel brand cards and manages brand data.
 * Handles logic for extracting and displaying brand card lists and logos.
 */
"use client";

import { cn, generateLinkFromGeneralLink, LinkConfig } from "@/lib/utils";
import React, { useState } from "react";

import {
  GetBrandCardDatasourceQuery,
  Maybe,
  Scalars,
  UploadFile,
} from "@/types/generated";
import Image from "next/image";
import { HotelBrandCard } from "./HotelBrandCard";

interface IHotelBrandsProps {
  containerClassName?: string;
  brandCardDatasource: GetBrandCardDatasourceQuery;
  locale: string;
}
export interface ButtonLink {
  type: "external";
  metadata: {
    url: string;
    target?: string;
  };
}

interface BrandCard {
  item_name: string;
  brand_logo?: UploadFile;
  display_name?: string;
  description?: Maybe<Scalars["String"]["output"]>;
  slider_images?: UploadFile[];
  order?: number | null;
  button_link?: ButtonLink;
  is_cover?: boolean;
}

const HotelBrands: React.FC<IHotelBrandsProps> = ({
  containerClassName = "",
  brandCardDatasource,
  locale,
}) => {
  // List of collection names for middle list extraction
  const LIST_NAME_COLLECTION = new Set([
    "CRM Middle List Datasource",
    "KAMENOI COLLECTION",
    "Mystays Collection",
  ]);
  // Extract brand card data from datasource
  const { homeBrand, sharedDatasourcesReusableBrandCardDatasources } =
    brandCardDatasource;
  const brandCardList =
    sharedDatasourcesReusableBrandCardDatasources[0]?.brand_card_items;
  // Find middle list item for special collections
  const middleListItem = homeBrand?.middle_list_datasources?.find(
    (item) =>
      LIST_NAME_COLLECTION.has(item?.item_name || "") ||
      item?.documentId === "98d2b6b08e7c9198dd87a922",
  );
  const logoList = [
    ...(homeBrand?.top_list_datasources?.[0]
      ?.home_brands_top_list_datasources_crm_top_list_datasource_top_details ??
      []),
    ...(middleListItem ? [middleListItem] : []),
    ...(homeBrand?.bottom_list_datasources?.[0]?.brand_details ?? []),
  ];

  const mergedBrandCardData: BrandCard[] = logoList
    .map((logo) => {
      const detail = brandCardList?.find(
        (card) => card?.item_name === logo?.item_name,
      );
      return {
        ...logo,
        ...detail,
      };
    })
    .filter((item): item is BrandCard => {
      return (
        item != null &&
        "brand_logo" in item &&
        item.brand_logo != null &&
        !!item.item_name
      );
    })
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  const firstBrand = mergedBrandCardData?.find(Boolean);
  const [activeBrandCard, setActiveBrandCard] = useState<string | null>(
    firstBrand?.item_name || null,
  );

  const handleToggleBrand = (itemName: string) => {
    setActiveBrandCard(itemName);
  };

  const rawTitle =
    sharedDatasourcesReusableBrandCardDatasources[0]?.title ?? "";
  const [headTitle, bottomTitle] = rawTitle.split(/<br\s*\/?>/);

  return (
    sharedDatasourcesReusableBrandCardDatasources && (
      <div
        className={cn(
          "flex w-full max-w-[1920px] flex-col gap-6 bg-[var(--fill-base-main)] px-4 py-10 lg:gap-0 lg:px-0 lg:py-0 lg:pt-[93px] lg:pb-[105px]",
          containerClassName,
        )}
      >
        {sharedDatasourcesReusableBrandCardDatasources[0]?.title && (
          <div className="text-center text-white">
            <div className="text-[18px] md:text-[25px]">{headTitle}</div>
            <h2 className="text-2xl font-[var(--font-semibold)] md:text-[35px]">
              {bottomTitle}
            </h2>
          </div>
        )}

        <div className={cn("relative w-full", containerClassName)}>
          <div
            className={cn(
              `mb-4 flex gap-1 overflow-x-scroll md:mt-9 lg:mt-10 lg:mb-15 lg:justify-center lg:gap-2 lg:overflow-x-auto`,
            )}
          >
            {mergedBrandCardData?.map((brandCard) =>
              brandCard ? (
                <div
                  key={brandCard?.item_name}
                  className="w-[140px] lg:w-[138px]"
                >
                  <button
                    onClick={() =>
                      handleToggleBrand(
                        brandCard?.item_name ? brandCard?.item_name : "",
                      )
                    }
                    className={cn(
                      "relative h-15 w-[140px] cursor-pointer rounded-lg bg-white transition lg:h-21",
                    )}

                    aria-label="brand selection button"
                  >
                    {activeBrandCard !== brandCard.item_name && (
                      <div className="absolute inset-0 z-10 rounded-lg bg-[var(--fill-base-main)]/60" />
                    )}
                    <Image
                      src={brandCard?.brand_logo?.cloudflare_url || ""}
                      fill
                      alt={brandCard?.brand_logo?.alternativeText || ""}
                      aria-label={brandCard?.brand_logo?.caption || ""}
                      className="rounded-lg object-contain p-2 mix-blend-multiply"
                      style={{ backgroundColor: "transparent" }}
                    />
                  </button>
                </div>
              ) : null,
            )}
          </div>
          {mergedBrandCardData?.map(
            (brandCard) =>
              brandCard &&
              activeBrandCard === brandCard?.item_name && (
                <HotelBrandCard
                  key={brandCard.item_name}
                  is_cover={brandCard?.is_cover ?? true}
                  brandLogo={brandCard.brand_logo}
                  brandDesktopImage={brandCard?.slider_images}
                  brandMobileImage={brandCard?.slider_images}
                  description={
                    brandCard?.description ? brandCard.description : ""
                  }
                  brandUrl={generateLinkFromGeneralLink(
                    brandCard?.button_link as LinkConfig,
                    locale,
                  )}
                  target={brandCard?.button_link?.metadata?.target ?? "_parent"}
                  lessLinkText={
                    sharedDatasourcesReusableBrandCardDatasources[0]
                      ?.show_less_label || ""
                  }
                  moreLinkText={
                    sharedDatasourcesReusableBrandCardDatasources[0]
                      ?.show_more_label || ""
                  }
                  buttonText={
                    sharedDatasourcesReusableBrandCardDatasources[0]
                      ?.button_text || ""
                  }
                />
              ),
          )}
        </div>
      </div>
    )
  );
};

export { HotelBrands };
